import React, { createContext, useContext, useState, useEffect } from 'react';
import storage from '../utils/storage';

// Theme Context
const ThemeContext = createContext();

// Theme Colors
export const lightTheme = {
  // Background Colors
  background: '#F8FAFC',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  
  // Text Colors
  text: '#1A202C',
  textSecondary: '#718096',
  textMuted: '#CBD5E0',
  
  // Primary Colors
  primary: '#4A90E2',
  primaryLight: '#E6F3FF',
  primaryDark: '#4A90E2', // Same as primary for light mode
  accent: '#7ED321',
  
  // Status Colors
  success: '#7ED321',
  warning: '#F6AD55',
  error: '#F56565',
  info: '#4A90E2',
  
  // Border Colors
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  
  // Shadow
  shadow: '#000000',
  
  // Input Colors
  inputBackground: '#F8FAFC',
  inputBorder: '#E2E8F0',
  inputText: '#1A202C',
  
  // Button Colors
  buttonPrimary: '#4A90E2',
  buttonSecondary: '#E2E8F0',
  buttonText: '#FFFFFF',
  buttonTextSecondary: '#718096',
};

export const darkTheme = {
  // Background Colors
  background: '#0F172A',
  surface: '#1E293B',
  card: '#334155',
  
  // Text Colors
  text: '#F8FAFC',
  textSecondary: '#CBD5E0',
  textMuted: '#64748B',
  
  // Primary Colors
  primary: '#60A5FA',
  primaryLight: '#1E3A8A',
  primaryDark: '#1E40AF', // Much darker blue for buttons in dark mode
  accent: '#34D399',
  
  // Status Colors
  success: '#34D399',
  warning: '#FBBF24',
  error: '#F87171',
  info: '#60A5FA',
  
  // Border Colors
  border: '#475569',
  borderLight: '#334155',
  
  // Shadow
  shadow: '#000000',
  
  // Input Colors
  inputBackground: '#1E293B',
  inputBorder: '#475569',
  inputText: '#F8FAFC',
  
  // Button Colors
  buttonPrimary: '#60A5FA',
  buttonSecondary: '#475569',
  buttonText: '#FFFFFF',
  buttonTextSecondary: '#CBD5E0',
};

// Theme Provider
export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference from storage
  useEffect(() => {
    loadThemePreference();
  }, []);

  const loadThemePreference = async () => {
    try {
      const savedTheme = await storage.getItem('themePreference');
      if (savedTheme !== null) {
        setIsDarkMode(JSON.parse(savedTheme));
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTheme = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      await storage.setItem('themePreference', JSON.stringify(newTheme));
    } catch (error) {
      // Silently handle error
    }
  };

  const setTheme = async (darkMode) => {
    try {
      setIsDarkMode(darkMode);
      await storage.setItem('themePreference', JSON.stringify(darkMode));
    } catch (error) {
      // Silently handle error
    }
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    isDarkMode,
    theme,
    toggleTheme,
    setTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;